/**
 * Debug instrumentation library — the foundation of CipherOps's
 * "instrument from day one" working practice (see
 * latest/ENGINEERING-PRACTICES.md).
 *
 * Provides:
 *   - State registry — useDebugRegister(name, state) so each screen
 *     can expose its current React state to the DebugPanel
 *   - Event log — debugLog.recordEvent({ type, description, data? })
 *     for higher-level user actions (route changes, mutations, etc.)
 *   - API call ledger — populated by the wrapped invoke() in
 *     packages/data-source/src/remote.ts (planned integration)
 *   - Error capture — Error Boundary integration captures the last
 *     state + last 10 API calls when a render error fires
 *   - Bundle export — serialise everything as a JSON file the user
 *     can attach to a support email
 *
 * Why this exists:
 *   - Self-host customers we can't SSH into need to be able to
 *     export a bundle when they hit a bug
 *   - Our own dev loop benefits from always-on observability — no
 *     more "let me add a console.log and reproduce"
 *
 * Memory bound:
 *   - api_calls and events are ring-buffered to 50 / 100 entries
 *   - errors retain everything captured this session (rarely many)
 *   - State registry holds only currently-mounted screens
 *
 * Singleton: this module exports a single debugStore instance. The
 * DebugPanel component subscribes to changes via subscribeDebugStore().
 * In tests, call resetDebugStoreForTests() in beforeEach.
 */

import { useEffect } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface ApiCallEntry {
  /** Channel name, e.g. "cipherops/findings/list" */
  channel: string;
  /** HTTP path mapped from channel, e.g. "/api/v1/findings/list" */
  path?: string;
  /** Request body (will be stringified for export; may be truncated) */
  payload?: unknown;
  /** Response body, present on success */
  response?: unknown;
  /** Error object, present on failure (Error.message + .name + .code captured) */
  error?: { name: string; message: string; code?: string };
  /** Request id from the X-Request-Id header — for log correlation */
  requestId?: string;
  /** Duration in milliseconds */
  durationMs: number;
  /** True if call succeeded (200-299) */
  ok: boolean;
  /** HTTP status code if available */
  status?: number;
  /** ISO timestamp when call started */
  startedAt: string;
}

export interface DebugEvent {
  /** Short machine-readable type, e.g. "route_change", "trial_banner_escalation" */
  type: string;
  /** Human-readable one-line description */
  description: string;
  /** Optional structured data */
  data?: Record<string, unknown>;
  /** ISO timestamp */
  at: string;
}

export interface ErrorEntry {
  /** Error message */
  message: string;
  /** Error name (TypeError, IpcError, etc.) */
  name: string;
  /** Stack trace */
  stack?: string;
  /** React component stack (from Error Boundary) */
  componentStack?: string;
  /** Snapshot of the state registry at time of error */
  stateSnapshot?: Record<string, unknown>;
  /** Last 10 API calls before the error */
  recentApiCalls?: ApiCallEntry[];
  /** Last 20 events before the error */
  recentEvents?: DebugEvent[];
  /** ISO timestamp */
  at: string;
}

export interface PerformanceEntry {
  name: string;
  durationMs: number;
  /** ISO timestamp */
  at: string;
  category?: string;
}

export interface SessionInfo {
  user_id?: string;
  email?: string;
  display_name?: string;
  firm_id?: string;
  firm_name?: string;
  role?: string;
  tier?: string;
  subscription_status?: string;
  trial_ends_at?: string;
  auth_provider?: string;
  instance_version?: string;
  /** Backend health: green if last /health was <30s ago */
  backend_healthy?: boolean;
  /** Request id for the current page load */
  page_request_id?: string;
}

/**
 * The full debug snapshot — what Export Bundle produces.
 */
export interface DebugBundle {
  exported_at: string;
  instance_version?: string;
  instance_url: string;
  user_agent: string;
  session: SessionInfo;
  state_registry: Record<string, unknown>;
  api_calls: ApiCallEntry[];
  events: DebugEvent[];
  errors: ErrorEntry[];
  performance: PerformanceEntry[];
}

// ============================================================================
// Store
// ============================================================================

const MAX_API_CALLS = 50;
const MAX_EVENTS = 100;
const MAX_PERFORMANCE_ENTRIES = 200;

interface DebugStore {
  session: SessionInfo;
  stateRegistry: Record<string, unknown>;
  apiCalls: ApiCallEntry[];
  events: DebugEvent[];
  errors: ErrorEntry[];
  performance: PerformanceEntry[];
}

const store: DebugStore = {
  session: {},
  stateRegistry: {},
  apiCalls: [],
  events: [],
  errors: [],
  performance: [],
};

type StoreSubscriber = () => void;
const subscribers = new Set<StoreSubscriber>();

function notifySubscribers(): void {
  for (const sub of subscribers) sub();
}

/**
 * Subscribe to any store change. Returns an unsubscribe fn.
 * The DebugPanel uses this to re-render when new data arrives.
 */
export function subscribeDebugStore(fn: StoreSubscriber): () => void {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

/** Read-only snapshot for the panel to render. */
export function getDebugStore(): DebugStore {
  return store;
}

// ============================================================================
// Session info — updated by the auth bootstrap on sign-in
// ============================================================================

export function setSession(info: Partial<SessionInfo>): void {
  store.session = { ...store.session, ...info };
  notifySubscribers();
}

// ============================================================================
// State registry — useDebugRegister hook
// ============================================================================

/**
 * Register a screen's state for inspection in the Debug Panel.
 *
 * Usage:
 *   function MyScreen() {
 *     const [state, setState] = useState({ ... });
 *     useDebugRegister('MyScreen', state);
 *     return <div data-debug-id="my-screen">...</div>;
 *   }
 *
 * On unmount the entry is removed. If the same name is registered by
 * multiple components simultaneously, the latest call wins (subsequent
 * mounts overwrite). This is intentional — a screen is the unit, not
 * a component instance.
 */
export function useDebugRegister(name: string, state: unknown): void {
  useEffect(() => {
    store.stateRegistry[name] = state;
    notifySubscribers();
    return () => {
      delete store.stateRegistry[name];
      notifySubscribers();
    };
  }, [name, state]);
}

// ============================================================================
// Event log — debugLog.recordEvent / recordApiCall / recordError
// ============================================================================

export const debugLog = {
  recordEvent(event: Omit<DebugEvent, 'at'>): void {
    store.events.push({ ...event, at: new Date().toISOString() });
    if (store.events.length > MAX_EVENTS) {
      store.events.shift();
    }
    notifySubscribers();
  },

  recordApiCall(call: Omit<ApiCallEntry, 'startedAt'> & { startedAt?: string }): void {
    store.apiCalls.push({
      ...call,
      startedAt: call.startedAt ?? new Date().toISOString(),
    });
    if (store.apiCalls.length > MAX_API_CALLS) {
      store.apiCalls.shift();
    }
    notifySubscribers();
  },

  recordError(err: Omit<ErrorEntry, 'at' | 'stateSnapshot' | 'recentApiCalls' | 'recentEvents'>): void {
    store.errors.push({
      ...err,
      // Snapshot state + last 10 API calls + last 20 events at error time
      stateSnapshot: JSON.parse(JSON.stringify(store.stateRegistry)) as Record<string, unknown>,
      recentApiCalls: store.apiCalls.slice(-10),
      recentEvents: store.events.slice(-20),
      at: new Date().toISOString(),
    });
    notifySubscribers();
  },

  recordPerformance(entry: Omit<PerformanceEntry, 'at'>): void {
    store.performance.push({ ...entry, at: new Date().toISOString() });
    if (store.performance.length > MAX_PERFORMANCE_ENTRIES) {
      store.performance.shift();
    }
    notifySubscribers();
  },
};

// ============================================================================
// Bundle export
// ============================================================================

/**
 * Produce a DebugBundle for export. Sanitises out anything that's
 * obviously sensitive (passwords, tokens) but does NOT redact firm
 * data — the user is exporting their own bundle.
 */
export function exportBundle(): DebugBundle {
  return {
    exported_at: new Date().toISOString(),
    instance_version: store.session.instance_version,
    instance_url: typeof window !== 'undefined' ? window.location.origin : 'unknown',
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    session: { ...store.session },
    state_registry: redactSensitive(JSON.parse(JSON.stringify(store.stateRegistry))) as Record<string, unknown>,
    api_calls: store.apiCalls.map((c) => ({
      ...c,
      payload: redactSensitive(c.payload),
      response: redactSensitive(c.response),
    })),
    events: [...store.events],
    errors: [...store.errors],
    performance: [...store.performance],
  };
}

/**
 * Trigger a browser download of the bundle as a timestamped JSON file.
 */
export function downloadBundle(): void {
  if (typeof window === 'undefined') return;
  const bundle = exportBundle();
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `cipherops-debug-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  debugLog.recordEvent({ type: 'debug_bundle_exported', description: 'Debug bundle downloaded' });
}

// ============================================================================
// PII / sensitive value redaction
// ============================================================================

const SENSITIVE_KEY_PATTERNS = [
  /password/i,
  /token/i,
  /secret/i,
  /api[_-]?key/i,
  /authorization/i,
  /cookie/i,
  /^card[_-]?number$/i,
  /^cvc$/i,
  /^cvv$/i,
];

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY_PATTERNS.some((re) => re.test(key));
}

/**
 * Recursively walks an object and replaces values under sensitive
 * keys with "[REDACTED]". Returns a deep copy — does not mutate input.
 */
export function redactSensitive(value: unknown, depth = 0): unknown {
  if (depth > 10) return '[max-depth]';
  if (value === null || value === undefined) return value;
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value.map((v) => redactSensitive(v, depth + 1));
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (isSensitiveKey(k)) {
      out[k] = '[REDACTED]';
    } else {
      out[k] = redactSensitive(v, depth + 1);
    }
  }
  return out;
}

// ============================================================================
// Reset (tests only)
// ============================================================================

export function resetDebugStoreForTests(): void {
  store.session = {};
  store.stateRegistry = {};
  store.apiCalls = [];
  store.events = [];
  store.errors = [];
  store.performance = [];
  notifySubscribers();
}
